import { type ChangeEvent, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isNil, values } from 'lodash';
// Import UI Components
import { RadioGroup, Icon } from '@geneui/components';
// Import Types
import { RadioTab } from './types';
// Import Styles
import styles from './radioTabGroup.module.scss';

interface RadioTabGroupProps<T extends string> {
    activeTab: T;
    setActiveTab: (value: T) => void;
    tabs: Record<T, RadioTab<T>>;
    name?: string;
    className?: string;
}

const RadioTabGroup = <T extends string>({
    activeTab,
    setActiveTab,
    tabs,
    name = 'controlled',
    className = '',
}: RadioTabGroupProps<T>) => {
    const { t } = useTranslation();

    const options = useMemo(
        () =>
            values(tabs).map(({ type, iconType, titleKey }) => ({
                value: type,
                label: isNil(iconType) ? (
                    t(titleKey)
                ) : (
                    <div className={styles.controlledIconBlock}>
                        <Icon type={iconType} />
                        {t(titleKey)}
                    </div>
                ),
            })),
        [tabs, t],
    );

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setActiveTab(e.target.value as T);
    };

    return (
        <RadioGroup
            className={className}
            name={name}
            options={options}
            type="tab"
            value={activeTab}
            onChange={handleChange}
        />
    );
};

export default memo(RadioTabGroup) as typeof RadioTabGroup;
